/**
 * EF-CREW-ONBOARDING-STATUS-AND-DOCUMENT-APPROVAL-V1
 * 
 * CandidateDocumentReview - Admin UI for reviewing and approving
 * crew candidate documents during onboarding.
 * 
 * Features:
 * - View all documents for a candidate
 * - Approve/Reject individual documents with reasons
 * - Shows document status summary
 * - Shows missing required documents
 * - Indicates when candidate is ready for activation
 */
import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import api from "../../lib/api";

// UI Components
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { Badge } from "../ui/badge";
import { Textarea } from "../ui/textarea";
import { ScrollArea } from "../ui/scroll-area";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alert-dialog";

// Icons
import {
  FileText,
  CheckCircle2,
  XCircle,
  Clock,
  Eye,
  Download,
  AlertTriangle,
  Loader2,
  Shield,
  ShieldAlert,
  ShieldCheck,
  ExternalLink,
} from "lucide-react";

// Endpoints
import { CREW_ENDPOINTS } from "../../api/endpoints";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

// Document status badge styles
const statusStyles = {
  pending: { bg: "bg-amber-500/20", text: "text-amber-400", icon: Clock },
  verified: { bg: "bg-emerald-500/20", text: "text-emerald-400", icon: ShieldCheck },
  approved: { bg: "bg-emerald-500/20", text: "text-emerald-400", icon: ShieldCheck },
  rejected: { bg: "bg-red-500/20", text: "text-red-400", icon: ShieldAlert },
};

function DocumentStatusBadge({ status }) {
  const style = statusStyles[status] || statusStyles.pending;
  const Icon = style.icon;
  
  return (
    <Badge variant="outline" className={`${style.bg} ${style.text} border-none`}>
      <Icon className="h-3 w-3 mr-1" />
      {status === "verified" ? "Approved" : status.charAt(0).toUpperCase() + status.slice(1)}
    </Badge>
  );
}

function DocumentCard({ document, onApprove, onReject, onPreview }) {
  // EF-FIX-DOCUMENT-PREVIEW-UNKNOWN-TYPE-V2: Use file_url for proper access
  const fileUrl = document.file_url 
    ? `${API_URL}${document.file_url}` 
    : `${API_URL}/api/admin/crew-documents/${document.id}/file`;
  
  return (
    <Card 
      className={`${
        document.status === "rejected" ? "border-red-500/30 bg-red-500/5" :
        document.status === "verified" ? "border-emerald-500/30 bg-emerald-500/5" :
        "border-border"
      }`}
      data-testid={`document-review-card-${document.id}`}
    >
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3 min-w-0">
            <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
              <FileText className="h-5 w-5 text-primary" />
            </div>
            <div className="min-w-0">
              <p className="font-medium truncate">{document.original_filename}</p>
              <p className="text-sm text-muted-foreground">
                {document.document_type_name || document.document_type_code || "General"}
              </p>
              <div className="flex items-center gap-2 mt-1">
                <DocumentStatusBadge status={document.status} />
                {document.uploaded_at && (
                  <span className="text-xs text-muted-foreground">
                    {new Date(document.uploaded_at || document.created_at).toLocaleDateString()}
                  </span>
                )}
              </div>
              {document.status === "rejected" && document.rejection_reason && (
                <p className="text-sm text-red-400 mt-2">
                  Reason: {document.rejection_reason}
                </p>
              )}
            </div>
          </div>
          
          <div className="flex items-center gap-2 flex-shrink-0">
            {/* Preview button */}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onPreview(document)}
              data-testid={`preview-document-${document.id}`}
            >
              <Eye className="h-4 w-4" />
            </Button>
            
            {/* Download button - EF-FIX-DOCUMENT-PREVIEW-UNKNOWN-TYPE-V2: Use file_url */}
            <Button
              variant="ghost"
              size="sm"
              asChild
            >
              <a 
                href={fileUrl} 
                target="_blank" 
                rel="noopener noreferrer"
                download
              >
                <Download className="h-4 w-4" />
              </a>
            </Button>
            
            {/* Approval actions (only for pending docs) */}
            {document.status === "pending" && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  className="bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20 border-emerald-500/30"
                  onClick={() => onApprove(document)}
                  data-testid={`approve-document-${document.id}`}
                >
                  <CheckCircle2 className="h-4 w-4 mr-1" />
                  Approve
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="bg-red-500/10 text-red-400 hover:bg-red-500/20 border-red-500/30"
                  onClick={() => onReject(document)}
                  data-testid={`reject-document-${document.id}`}
                >
                  <XCircle className="h-4 w-4 mr-1" />
                  Reject
                </Button>
              </>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export function CandidateDocumentReview({ candidateId, candidateName, onStatusChange }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [rejectDialog, setRejectDialog] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [processing, setProcessing] = useState(false);
  const [previewDoc, setPreviewDoc] = useState(null);

  const fetchDocuments = useCallback(async () => {
    if (!candidateId) return;
    
    try {
      setLoading(true);
      const res = await api.get(CREW_ENDPOINTS.getCandidateDocuments(candidateId));
      setData(res.data);
    } catch (err) {
      console.error("Failed to fetch candidate documents:", err);
      toast.error("Failed to load documents");
    } finally {
      setLoading(false);
    }
  }, [candidateId]);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  const handleApprove = async (document) => {
    try {
      setProcessing(true);
      await api.post(CREW_ENDPOINTS.approveDocument(document.id));
      toast.success("Document approved");
      fetchDocuments();
      onStatusChange?.();
    } catch (err) {
      console.error("Failed to approve document:", err);
      toast.error("Failed to approve document");
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!rejectDialog || !rejectReason.trim()) {
      toast.error("Please provide a rejection reason");
      return;
    }
    
    try {
      setProcessing(true);
      await api.post(CREW_ENDPOINTS.rejectDocument(rejectDialog.id), {
        rejection_reason: rejectReason.trim(),
      });
      toast.success("Document rejected");
      setRejectDialog(null);
      setRejectReason("");
      fetchDocuments();
      onStatusChange?.();
    } catch (err) {
      console.error("Failed to reject document:", err);
      toast.error("Failed to reject document");
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        No document data available
      </div>
    );
  }

  const { documents, summary, missing_required, ready_for_activation } = data;

  return (
    <div className="space-y-6" data-testid="candidate-document-review">
      {/* Summary Card */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            Document Review
          </CardTitle>
          <CardDescription>
            {candidateName ? `Reviewing documents for ${candidateName}` : "Reviewing candidate documents"}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-4 gap-4">
            <div className="text-center">
              <div className="text-2xl font-semibold">{summary?.total || 0}</div>
              <div className="text-xs text-muted-foreground">Total</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-semibold text-amber-400">{summary?.pending || 0}</div>
              <div className="text-xs text-muted-foreground">Pending</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-semibold text-emerald-400">{summary?.approved || 0}</div>
              <div className="text-xs text-muted-foreground">Approved</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-semibold text-red-400">{summary?.rejected || 0}</div>
              <div className="text-xs text-muted-foreground">Rejected</div>
            </div>
          </div>
          
          {/* Ready for activation indicator */}
          {ready_for_activation && (
            <div className="mt-4 p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30">
              <div className="flex items-center gap-2 text-emerald-400">
                <CheckCircle2 className="h-5 w-5" />
                <span className="font-medium">Ready for Activation</span>
              </div>
              <p className="text-sm text-emerald-400/80 mt-1">
                All required documents have been approved. You can now approve this candidate.
              </p>
            </div>
          )}
          
          {/* Missing required documents */}
          {missing_required && missing_required.length > 0 && (
            <div className="mt-4 p-3 rounded-lg bg-amber-500/10 border border-amber-500/30">
              <div className="flex items-center gap-2 text-amber-400">
                <AlertTriangle className="h-5 w-5" />
                <span className="font-medium">Missing Required Documents</span>
              </div>
              <ul className="text-sm text-amber-400/80 mt-2 list-disc list-inside">
                {missing_required.map((doc, idx) => (
                  <li key={idx}>{doc.document_type_name}</li>
                ))}
              </ul>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Documents List */}
      <div className="space-y-3">
        {documents.length === 0 ? (
          <Card>
            <CardContent className="py-8 text-center text-muted-foreground">
              <FileText className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p>No documents uploaded yet</p>
            </CardContent>
          </Card>
        ) : (
          documents.map((doc) => (
            <DocumentCard
              key={doc.id}
              document={doc}
              onApprove={handleApprove}
              onReject={(d) => setRejectDialog(d)}
              onPreview={(d) => setPreviewDoc(d)}
            />
          ))
        )}
      </div>

      {/* Reject Dialog */}
      <AlertDialog open={!!rejectDialog} onOpenChange={() => setRejectDialog(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reject Document?</AlertDialogTitle>
            <AlertDialogDescription>
              Please provide a reason for rejection. The crew member will need to upload a new document.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <Textarea
            placeholder="Enter rejection reason..."
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            className="min-h-[100px]"
            data-testid="rejection-reason-input"
          />
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleReject}
              disabled={!rejectReason.trim() || processing}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-reject-document"
            >
              {processing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Reject Document
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Preview Dialog */}
      <Dialog open={!!previewDoc} onOpenChange={() => setPreviewDoc(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle>{previewDoc?.original_filename}</DialogTitle>
            <DialogDescription>
              {previewDoc?.document_type_name || previewDoc?.document_type_code || "Document"}
            </DialogDescription>
          </DialogHeader>
          <div className="mt-4">
            {/* EF-FIX-DOCUMENT-PREVIEW-MIME-TYPE-MAPPING-V1: Use includes() for MIME type checks */}
            {(() => {
              if (!previewDoc) return null;
              
              // Build the proper file URL
              const fileUrl = previewDoc.file_url 
                ? `${API_URL}${previewDoc.file_url}` 
                : `${API_URL}/api/admin/crew-documents/${previewDoc.id}/file`;
              
              // EF-FIX-DOCUMENT-PREVIEW-MIME-TYPE-MAPPING-V1: Normalize mime_type
              const mime = (previewDoc.mime_type || "").toLowerCase();
              
              // Debug logging
              console.log("DOC PREVIEW:", { 
                id: previewDoc.id, 
                mime_type: previewDoc.mime_type,
                normalized_mime: mime,
                file_url: previewDoc.file_url,
                original_filename: previewDoc.original_filename 
              });
              
              // Fallback: infer from filename if mime is empty
              let effectiveMime = mime;
              if (!effectiveMime && previewDoc.original_filename) {
                const ext = previewDoc.original_filename.split('.').pop()?.toLowerCase();
                if (ext === 'pdf') effectiveMime = 'application/pdf';
                else if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'heic', 'heif'].includes(ext)) {
                  effectiveMime = `image/${ext === 'jpg' ? 'jpeg' : ext}`;
                }
              }
              
              // EF-FIX-DOCUMENT-PREVIEW-MIME-TYPE-MAPPING-V1: Use includes() for flexible matching
              if (effectiveMime.includes('image')) {
                return (
                  <img
                    src={fileUrl}
                    alt={previewDoc.original_filename}
                    className="max-w-full max-h-[60vh] mx-auto rounded-lg"
                    onError={(e) => {
                      console.log("Image load failed, showing fallback");
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'block';
                    }}
                  />
                );
              } else if (effectiveMime.includes('pdf')) {
                return (
                  <iframe
                    src={fileUrl}
                    className="w-full h-[60vh] rounded-lg border"
                    title="Document Preview"
                  />
                );
              } else {
                return (
                  <div className="text-center py-8 text-muted-foreground">
                    <FileText className="h-16 w-16 mx-auto mb-4 opacity-50" />
                    <p>Preview not available for this file type</p>
                    <p className="text-xs mt-1 opacity-70">{effectiveMime || 'Unknown type'}</p>
                    <Button
                      variant="outline"
                      className="mt-4"
                      asChild
                    >
                      <a 
                        href={fileUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        download={previewDoc.original_filename}
                      >
                        <Download className="h-4 w-4 mr-2" />
                        Download
                      </a>
                    </Button>
                  </div>
                );
              }
            })()}
            {/* Fallback error message (hidden by default) */}
            <div className="text-center py-8 text-muted-foreground" style={{display: 'none'}}>
              <FileText className="h-16 w-16 mx-auto mb-4 opacity-50" />
              <p>Preview failed to load</p>
              <Button
                variant="outline"
                className="mt-4"
                asChild
              >
                <a 
                  href={previewDoc?.file_url ? `${API_URL}${previewDoc.file_url}` : `${API_URL}/api/admin/crew-documents/${previewDoc?.id}/file`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <ExternalLink className="h-4 w-4 mr-2" />
                  Open in New Tab
                </a>
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default CandidateDocumentReview;
